import { useEffect, useState } from 'react';
import { LocalStorageKey } from '../constants/localStorageKey';
import { ExclusionListDate, Kpi, Store, StoreDetails } from '../global-state/types';
import { useUser } from './useController';
import { useLocalStorage } from './useLocalStorage';

const defaultStoreInformation: StoreInformation = {
  currencyCode: '',
  areaCode: '',
  areaName: '',
  timezone: '',
  kpis: [],
  storeName: '',
  corporateBrandId: 0,
  countryCode: '',
  exclusionListCount: [],
};

type StoreInformation = {
  currencyCode: string;
  areaCode: string;
  areaName: string;
  timezone: string;
  kpis: Kpi[];
  storeName: string;
  corporateBrandId: number;
  countryCode: string;
  exclusionListCount: ExclusionListDate[];
};

export const useStoreInformation = (): StoreInformation => {
  const {
    get: { currentStoreId, stores, storesDetails },
  } = useUser();

  const { getItem } = useLocalStorage();
  const selectedView = getItem(LocalStorageKey.SelectedView);

  const getStoreInformation = (newStores: Store[], newStoresDetails: StoreDetails[]): StoreInformation => {
    const currentStore = newStores.find((store) => store.storeId === currentStoreId);
    const currentStoreDetails = newStoresDetails.find((storeDetails) => storeDetails.storeId === currentStoreId);

    if (!currentStore) return defaultStoreInformation;

    let currencyCode = currentStore.currency || '';

    if (selectedView === 'area') {
      const filteredStoresByArea = newStoresDetails.filter((store) => store.area === currentStore.area);
      const areaCurrencies = filteredStoresByArea.map((store) => store.currency).filter(Boolean);
      const uniqueAreaCurrencies = [...new Set(areaCurrencies).values()];

      if (uniqueAreaCurrencies.length === 1) {
        currencyCode = uniqueAreaCurrencies[0];
      } else {
        currencyCode = 'SEK'; // Default to SEK if multiple currencies are found
      }
    }

    return {
      currencyCode,
      areaName: currentStore.area || '',
      areaCode: currentStore.hmRegion || '',
      timezone: currentStoreDetails ? currentStoreDetails.timezone : '',
      kpis: currentStore.kpis || [],
      storeName: currentStore.popularName || '',
      corporateBrandId: currentStore.corporateBrandId || 0,
      countryCode: currentStore.countryCode || '',
      exclusionListCount: currentStore.exclusionListCount || [],
    };
  };

  const [storeInformation, setStoreInformation] = useState<StoreInformation>(
    getStoreInformation(stores, storesDetails),
  );

  useEffect(() => {
    setStoreInformation(getStoreInformation(stores, storesDetails));
  }, [currentStoreId, stores]);

  return storeInformation;
};
